<template>
    <div class="relative">
        <UiSliderButtonPrev
            v-if="displayButtons"
            :id="prevButtonId"
            :disabled="current === 0"
            :aria-controls="trackId"
            aria-label="Previous slide"
            @click="goPrev" />
        <div :id="trackId" ref="container" class="keen-slider not-initialized !overflow-visible">
            <slot />
        </div>
        <UiSliderButtonNext
            v-if="displayButtons"
            :id="nextButtonId"
            :disabled="current === lastItem"
            :aria-controls="trackId"
            aria-label="Next slide"
            @click="goNext" />
    </div>
</template>

<script setup lang="ts">
    import { useKeenSlider } from 'keen-slider/vue';

    const instanceId = useUniqueId('slider');
    const trackId = `${instanceId}-track`;
    const prevButtonId = `${instanceId}-prev`;
    const nextButtonId = `${instanceId}-next`;

    const current = ref(0);
    const lastItem = ref(1);
    const props = withDefaults(defineProps<{ displayButtons?: boolean; slides?: any; breakpoints?: any }>(), {
        displayButtons: true,
        spacing: { perView: 'auto', spacing: 16 },
        breakpoints: {
            '(min-width: 0px)': {
                slides: { perView: 'auto', spacing: 16 },
            },
            '(min-width: 1024px)': {
                slides: { perView: 'auto', spacing: 48 },
            },
            '(min-width: 1600px)': {
                slides: { perView: 'auto', spacing: 76 },
            },
        },
    });

    function update(): void {
        if (slider.value) {
            slider.value.update();
        }
    }

    defineExpose({ update });

    function goNext(): void {
        slider.value?.next();
    }

    function goPrev(): void {
        slider.value?.prev();
    }

    // Horizontal scroll keen slider
    const WheelControls = slider => {
        let touchTimeout;
        let position;
        let wheelActive;
        let eventWheel: ((e: WheelEvent) => void) | undefined;

        function dispatch(e, name) {
            position.x -= e.deltaX;
            position.y -= e.deltaY;
            slider.container.dispatchEvent(
                new CustomEvent(name, {
                    detail: {
                        x: position.x,
                        y: position.y,
                    },
                }),
            );
        }

        function wheelStart(e) {
            position = {
                x: e.pageX,
                y: e.pageY,
            };
            dispatch(e, 'ksDragStart');
        }

        function wheel(e) {
            dispatch(e, 'ksDrag');
        }

        function wheelEnd(e) {
            dispatch(e, 'ksDragEnd');
        }

        eventWheel = function eventWheel(e: WheelEvent) {
            // Check the scroll direction
            const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);

            // Prevent default behavior only for horizontal scroll
            if (isHorizontalScroll) {
                e.preventDefault();
                if (!wheelActive) {
                    wheelStart(e);
                    wheelActive = true;
                }
                wheel(e);
                clearTimeout(touchTimeout);
                touchTimeout = setTimeout(() => {
                    wheelActive = false;
                    wheelEnd(e);
                }, 50);
            }
        };

        slider.on('created', () => {
            slider.container.addEventListener('wheel', eventWheel, {
                passive: false,
            });
        });

        slider.on('destroyed', () => {
            if (eventWheel) {
                slider.container.removeEventListener('wheel', eventWheel);
            }
        });
    };

    // Keen slider
    const [container, slider] = useKeenSlider(
        {
            initial: current.value,
            slideChanged: s => {
                current.value = s.track.details.rel;
                lastItem.value = s.track.details.maxIdx;
            },
            loop: false,
            slides: props.slides,
            breakpoints: props.breakpoints,
            renderMode: 'performance',
            rubberband: false,
            mode: 'free-snap',
            dragSpeed: 0.5,
            created(s) {
                // Remove the not-initialized class when slider is created
                s.container.classList.remove('not-initialized');
            },
        },
        [WheelControls],
    );
</script>

<style scoped>
    .keen-slider :deep(.keen-slider__slide) {
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        will-change: transform;
    }

    .keen-slider :deep(.keen-slider__slide img) {
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        transform: translateZ(0);
    }
</style>
